import React from "react";
import { FaUser, FaCheck, FaMinusCircle } from "react-icons/fa";

import { IPlayer } from "../../types";
import {
  PlayerPickerOverlay,
  PlayerPickerModal as PlayerPickerModalStyled,
  PlayerPickerHeader,
  PlayerPickerTitle,
  PlayerPickerCloseBtn,
  PlayerPickerList,
  PlayerPickerItem,
  PlayerPickerAvatar,
  PlayerPickerAvatarImg,
  PlayerPickerInfo,
  PlayerPickerName,
  PlayerPickerTeam,
  PlayerPickerCheck,
} from "../../styledComponents";

interface IPlayerStats {
  kills: number;
  deaths: number;
}

export interface IPlayerPickerModalProps {
  show: boolean;
  onClose: () => void;
  sortedPlayers: IPlayer[];
  ctPlayerCount: number;
  currentUserSteamId: string | null;
  selectedPlayers: Set<string>;
  playerStats: Record<string, IPlayerStats>;
  getPlayerName: (steamId: string) => string;
  addPlayerToSelection: (playerId: string) => void;
}

export const PlayerPickerModal: React.FC<IPlayerPickerModalProps> = ({
  show,
  onClose,
  sortedPlayers,
  ctPlayerCount,
  currentUserSteamId,
  selectedPlayers,
  playerStats,
  getPlayerName,
  addPlayerToSelection,
}) => {
  if (!show) return null;

  const sortedByCurrentUser = [...sortedPlayers].sort((a, b) => {
    const aIsCurrentUser = a.steamId === currentUserSteamId;
    const bIsCurrentUser = b.steamId === currentUserSteamId;
    if (aIsCurrentUser && !bIsCurrentUser) return -1;
    if (!aIsCurrentUser && bIsCurrentUser) return 1;
    return 0;
  });

  return (
    <PlayerPickerOverlay onClick={onClose}>
      <PlayerPickerModalStyled onClick={(e) => e.stopPropagation()}>
        <PlayerPickerHeader>
          <PlayerPickerTitle>Select a Player</PlayerPickerTitle>
          <PlayerPickerCloseBtn onClick={onClose}>
            <FaMinusCircle size={18} />
          </PlayerPickerCloseBtn>
        </PlayerPickerHeader>
        <PlayerPickerList>
          {sortedByCurrentUser.map((player) => {
            const isAlreadySelected = selectedPlayers.has(player.steamId);
            const originalIndex = sortedPlayers.findIndex(
              (p) => p.steamId === player.steamId,
            );
            const isCTPlayer = originalIndex < ctPlayerCount;
            const isCurrentUser = player.steamId === currentUserSteamId;
            const stats = playerStats[player.steamId];

            return (
              <PlayerPickerItem
                key={player.steamId}
                $team={isCTPlayer ? "ct" : "t"}
                $isCurrentUser={isCurrentUser}
                $isSelected={isAlreadySelected}
                onClick={() => {
                  if (!isAlreadySelected) {
                    addPlayerToSelection(player.steamId);
                  }
                }}
              >
                <PlayerPickerAvatar
                  $team={isCTPlayer ? "ct" : "t"}
                  $isCurrentUser={isCurrentUser}
                >
                  {player.avatar ? (
                    <PlayerPickerAvatarImg src={player.avatar} alt="" />
                  ) : (
                    <FaUser size={14} />
                  )}
                </PlayerPickerAvatar>
                <PlayerPickerInfo>
                  <PlayerPickerName $isCurrentUser={isCurrentUser}>
                    {getPlayerName(player.steamId)}
                    {isCurrentUser && " (YOU)"}
                  </PlayerPickerName>
                  <PlayerPickerTeam $team={isCTPlayer ? "ct" : "t"}>
                    {isCTPlayer ? "Counter-Terrorists" : "Terrorists"} ·{" "}
                    {stats?.kills || 0}K / {stats?.deaths || 0}D
                  </PlayerPickerTeam>
                </PlayerPickerInfo>
                {isAlreadySelected && (
                  <PlayerPickerCheck>
                    <FaCheck size={14} />
                  </PlayerPickerCheck>
                )}
              </PlayerPickerItem>
            );
          })}
        </PlayerPickerList>
      </PlayerPickerModalStyled>
    </PlayerPickerOverlay>
  );
};

export default PlayerPickerModal;
